import { z } from 'zod';
import { safeLocalStorage } from '@pkgs/components/utils/storageUtils';
import { type PageTrackingTypes } from '@pkgs/components/types/tracking';
import { type InputContact, type Listing, PropertyMarketingType, type TrackingV2Response } from '@pkgs/api';

import type { LocalStorageTrackingProperty, TrackingIDs, TrackingProvider } from '@/types/tracking';
import { type PropertyTrackingIds } from '@/types/property.types';
import { SearchApiClient } from '@/consts/search-api';
import { COOKIE_KEY } from '@/consts/cookies';

import { getCookie } from './cookieUtils';

const ga4IDRegex = /^G-[\dA-Z]{10}$/;
const trackingValueSchema = z.union([z.string(), z.number(), z.null()]);
const trackingIdsSchema = z.record(z.string(), trackingValueSchema);
const localStorageTrackingPropertySchema = z.record(z.string(), trackingIdsSchema);

export const isGA4ID = (googleAnalytics4ID: string | undefined | null): boolean => {
  if (!googleAnalytics4ID) {
    return false;
  }
  return ga4IDRegex.test(googleAnalytics4ID);
};

/* when navigating from landing pages to search app, we add ev_tracking cookie with the Google Analytics account of
/ the licence partner to be able to track the user
/ this can be overwritten by passing a Google Analytics account as a query param ga=UA-XXXXX */
export const getLicencePartnerGAAccount = (): string => {
  let licencePartnerGAAccount = getCookie(COOKIE_KEY.evTracking);
  const urlSearchParams = new URLSearchParams(window.location.search);
  const gaQueryParam = urlSearchParams.get('ga');
  if (gaQueryParam) {
    licencePartnerGAAccount = gaQueryParam;
  }
  return licencePartnerGAAccount;
};

const pushTrackingObjectToDataLayer = (trackingIDs: TrackingIDs) => {
  for (const trackingProperty of Object.keys(trackingIDs)) {
    if (!trackingIDs[trackingProperty as TrackingProvider]) {
      continue;
    }

    switch (trackingProperty) {
      case 'licencePartnerCriteoID':
        window.dataLayer?.push({
          event: 'licencePartnerCriteoAccount',
          licencePartnerCriteoID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'licencePartnerFacebookID':
        window.dataLayer?.push({
          event: 'licencePartnerFacebookAccount',
          licencePartnerFacebookID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'licencePartnerGA4ID':
        window.dataLayer?.push({
          event: 'licencePartnerGA4Account',
          licencePartnerGA4ID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'licencePartnerLinkedInID':
        window.dataLayer?.push({
          event: 'licencePartnerLinkedInAccount',
          licencePartnerLinkedInID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'licencePartnerMicrosoftID':
        window.dataLayer?.push({
          event: 'licencePartnerMicrosoftAccount',
          licencePartnerMicrosoftID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'licencePartnerGoogleAdsID':
        window.dataLayer?.push({
          event: 'licencePartnerGoogleAdsAccount',
          licencePartnerGoogleAdsID: trackingIDs[trackingProperty] as string,
        });
        break;
      // Market Values from Spearhead project
      case 'marketCriteoID':
        window.dataLayer?.push({
          event: 'marketCriteoAccount',
          marketCriteoID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'marketFacebookID':
        window.dataLayer?.push({
          event: 'marketFacebookAccount',
          marketFacebookID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'marketGA4ID':
        window.dataLayer?.push({
          event: 'marketGA4Account',
          marketGA4ID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'marketLinkedInID':
        window.dataLayer?.push({
          event: 'marketLinkedInAccount',
          marketLinkedInID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'marketMicrosoftID':
        window.dataLayer?.push({
          event: 'marketMicrosoftAccount',
          marketMicrosoftID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'marketGoogleAdsID':
        window.dataLayer?.push({
          event: 'marketGoogleAdsAccount',
          marketGoogleAdsID: trackingIDs[trackingProperty] as string,
        });
        break;
      // Global Values from Spearhead project
      case 'globalCriteoID':
        window.dataLayer?.push({
          event: 'globalCriteoAccount',
          globalCriteoID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'globalFacebookID':
        window.dataLayer?.push({
          event: 'globalFacebookAccount',
          globalFacebookID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'globalGA4ID':
        window.dataLayer?.push({
          event: 'globalGA4Account',
          globalGA4ID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'globalLinkedInID':
        window.dataLayer?.push({
          event: 'globalLinkedInAccount',
          globalLinkedInID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'globalMicrosoftID':
        window.dataLayer?.push({
          event: 'globalMicrosoftAccount',
          globalMicrosoftID: trackingIDs[trackingProperty] as string,
        });
        break;
      case 'globalGoogleAdsID':
        window.dataLayer?.push({
          event: 'globalGoogleAdsAccount',
          globalGoogleAdsID: trackingIDs[trackingProperty] as string,
        });
        break;
      default:
        break;
    }
  }
};

export const mapPropertyTrackingIdsToDataLayerTrackingIds = (trackingIds: PropertyTrackingIds): TrackingIDs | null => {
  const allTrackingIds = {
    ...(trackingIds.googleAnalytics4ID && {
      licencePartnerGA4ID: trackingIds.googleAnalytics4ID,
    }),
    ...(trackingIds.googleAdsID && {
      licencePartnerGoogleAdsID: trackingIds.googleAdsID,
    }),
    ...(trackingIds.microsoftID && {
      licencePartnerMicrosoftID: trackingIds.microsoftID,
    }),
    ...(trackingIds.linkedInID && {
      licencePartnerLinkedInID: trackingIds.linkedInID,
    }),
    ...(trackingIds.criteoID && {
      licencePartnerCriteoID: trackingIds.criteoID,
    }),
    ...(trackingIds.facebookID && {
      licencePartnerFacebookID: trackingIds.facebookID,
    }),
  };
  return Object.keys(allTrackingIds).length ? allTrackingIds : null;
};

// priority when pushing tracking ids to GTM datalayer
// 1. tracking ids fetched based on the ga4Id url parameter
// 2. tracking ids coming from local storage
// 3. tracking ids coming from listing data (relevant only for expose page)
export const pushReferrerTrackingToDataLayer = ({
  propertyTrackingIds,
  urlTrackingIds,
}: {
  propertyTrackingIds?: TrackingIDs | null;
  urlTrackingIds?: TrackingIDs | null;
}): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // push tracking ids fetched based on ga4Id url param
  if (urlTrackingIds) {
    pushTrackingObjectToDataLayer(urlTrackingIds);
    return;
  }

  const storedTrackingProperties = safeLocalStorage.getItem('ev_dl_tracking');
  let trackingProperties: LocalStorageTrackingProperty = {};

  if (storedTrackingProperties) {
    try {
      const parsedTrackingProperties = localStorageTrackingPropertySchema.safeParse(JSON.parse(storedTrackingProperties));

      if (parsedTrackingProperties.success) {
        trackingProperties = parsedTrackingProperties.data;
      }
    } catch {
      trackingProperties = {};
    }
  }

  const referrerTracking = document.referrer ? trackingProperties[document.referrer] : trackingProperties['https://ev-tracking.test/en']; // custom url to test manually in stage

  // push tracking ids coming from local storage
  // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (referrerTracking) {
    pushTrackingObjectToDataLayer(referrerTracking);
    return;
  }

  // push tracking ids coming from the listing data
  if (propertyTrackingIds) {
    pushTrackingObjectToDataLayer(propertyTrackingIds);
    return;
  }
};

export const mapBFFTrackingResponseToPropertyTrackingIds = (trackingIds: TrackingV2Response): PropertyTrackingIds => {
  const { criteoId, facebookId, shopGa4Id, linkedInId, licensePartnerId, shopId, microsoftId, adwordsId } = trackingIds;
  return {
    ...(criteoId && { criteoID: criteoId }),
    ...(facebookId && { facebookID: facebookId }),
    ...(shopGa4Id && { googleAnalytics4ID: shopGa4Id }),
    ...(linkedInId && { linkedInID: linkedInId }),
    ...(licensePartnerId && { masterDataLicensePartnerID: licensePartnerId }),
    ...(shopId && { masterDataShopID: shopId }),
    ...(microsoftId && { microsoftID: microsoftId }),
    ...(adwordsId && { googleAdsID: adwordsId }),
  };
};

export const fetchTrackingIDs = async (googleAnalytics4ID: string): Promise<PropertyTrackingIds | null> => {
  try {
    const trackingIds = await SearchApiClient.getTrackingByGa4Id(googleAnalytics4ID);
    return mapBFFTrackingResponseToPropertyTrackingIds(trackingIds);
  } catch {
    return null;
  }
};

export const getPhoneLinkTrackingEventName = (trigger: string, index: number, phoneNumberCount: number) => {
  if (phoneNumberCount > 1) {
    return `${trigger}_link-${index + 1}_call`;
  } else {
    return `${trigger}_link_call`;
  }
};

export const trackGTMLeadEvent = ({
  requestData,
  listing,
  pageTrackingTypes,
  sessionEmailAddress,
}: {
  requestData: InputContact;
  listing: Listing;
  pageTrackingTypes: PageTrackingTypes;
  sessionEmailAddress: string | undefined;
}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'lead_created',
    source: 'new-search_expose_contact-form',
    flow: 'buyer',
    intention: listing.propertyMarketingType === PropertyMarketingType.rent ? 'rent' : 'buy',
    templateType: pageTrackingTypes.templateType,
    pageType: pageTrackingTypes.pageType,
    email: sessionEmailAddress ?? requestData.email,
  });
};
